import type { ScrivitoAccessor } from './entity-serializer'
import {
  getEntityRegistry,
  isEntityEnabled,
} from '@/Atomic/Widgets/AiChatBot/core/engine/entity-registry'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import type {
  EntityClassName,
  ResolvedEntity,
  SessionContext,
} from '@/Atomic/Widgets/AiChatBot/core/engine/types'

function getDisplayName(item: ScrivitoAccessor): string {
  const value = item.get('displayTitle')
  return typeof value === 'string' && value.length > 0 ? value : item.id()
}

export async function resolveEntityReference(
  entityType: EntityClassName | null,
  query: string,
  ctx: SessionContext,
): Promise<ResolvedEntity[]> {
  const cacheKey = `${entityType ?? 'global'}:${query.toLowerCase()}`
  const cached = ctx.resolveCache.get(cacheKey)
  if (cached) return cached

  const results = await resolveWithoutCache(entityType, query, ctx.featureFlags)
  ctx.resolveCache.set(cacheKey, results)
  return results
}

async function resolveWithoutCache(
  entityType: EntityClassName | null,
  query: string,
  featureFlags: Set<string>,
): Promise<ResolvedEntity[]> {
  if (entityType === null) {
    return resolveGlobal(query, featureFlags)
  }
  return resolvePerEntity(entityType, query, featureFlags)
}

async function resolveGlobal(
  query: string,
  featureFlags: Set<string>,
): Promise<ResolvedEntity[]> {
  const registry = getEntityRegistry()
  const searchableClasses = Object.entries(registry)
    // Gate by enabled capabilities so the fan-out never searches entity types
    // this instance lacks the feature flag for — those endpoints 404.
    .filter(
      ([dc, meta]) => meta?.searchable && isEntityEnabled(dc, featureFlags),
    )
    .map(([dc]) => dc)

  const settled = await Promise.allSettled(
    searchableClasses.map((dc) => resolvePerEntity(dc, query, featureFlags)),
  )
  const perEntityResults = settled
    .filter(
      (r): r is PromiseFulfilledResult<ResolvedEntity[]> =>
        r.status === 'fulfilled',
    )
    .map((r) => r.value)
  const seen = new Set<string>()
  return perEntityResults.flat().filter((r) => {
    const key = `${r.entityType}:${r.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

async function resolvePerEntity(
  dc: EntityClassName,
  query: string,
  featureFlags: Set<string>,
): Promise<ResolvedEntity[]> {
  if (!getEntityRegistry()[dc]?.searchable) return []
  if (!isEntityEnabled(dc, featureFlags)) return []

  const rawItems = await getHostRuntime().data.query(dc, {
    search: query,
    limit: 5,
  })

  // warning: adapter returns unknown[]; items are DataItem-like accessors with .id()/.get()
  const items = rawItems as ScrivitoAccessor[]

  if (items.length === 0) return []

  if (items.length === 1) {
    const singleItem = items[0]
    if (singleItem === undefined) return []
    const url = await resolveUrl(singleItem)
    return [
      {
        id: singleItem.id(),
        title: getDisplayName(singleItem),
        entityType: dc,
        confidence: 'exact',
        url,
      },
    ]
  }

  const queryLower = query.toLowerCase()
  const exactItem = items.find(
    (item) => getDisplayName(item).toLowerCase() === queryLower,
  )
  if (exactItem !== undefined) {
    const url = await resolveUrl(exactItem)
    return [
      {
        id: exactItem.id(),
        title: getDisplayName(exactItem),
        entityType: dc,
        confidence: 'exact',
        url,
      },
    ]
  }

  return Promise.all(
    items.map(async (item) => {
      const url = await resolveUrl(item)
      return {
        id: item.id(),
        title: getDisplayName(item),
        entityType: dc,
        confidence: 'ambiguous' as const,
        url,
      }
    }),
  )
}

async function resolveUrl(item: object): Promise<string | undefined> {
  try {
    return await getHostRuntime().navigation.urlFor(item)
  } catch {
    return undefined
  }
}
